import { useEffect } from 'react';

import { type Location, useLocation, useNavigate } from '@suntex.core/nav';
import { useAuth } from '@suntex.fim/auth/react';
import { useHomepageRedirect } from '@suntex.fim/framework';

import { handleNavigation } from './handleNavigation';

export interface HomeRedirectState {
  isLoading: boolean;
  isAuthenticated: boolean;
}

/**
 * Shared home-route redirect: sends an authenticated user to the dashboard that
 * matches their login type (or the deep link they were bounced from), and
 * triggers the external homepage redirect otherwise. Callers render their own
 * fallback UI based on the returned state.
 */
export function useHomeRedirect(): HomeRedirectState {
  const navigate = useNavigate();
  const location = useLocation() as Location<{ path?: string } | null>;
  const { isAuthenticated, isLoading, login } = useAuth();
  const { redirectUrl, setRedirectEnabled } = useHomepageRedirect();

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated && login) {
        if (location.state?.path) {
          navigate(location.state.path, { replace: true });
        } else {
          handleNavigation(login, navigate);
        }
        return () => {
          setRedirectEnabled(true);
        };
      } else if (redirectUrl) {
        window.location.replace(redirectUrl);
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    location,
    login,
    navigate,
    redirectUrl,
    setRedirectEnabled,
  ]);

  return { isLoading, isAuthenticated };
}
